import React, { useState, useEffect, useMemo } from 'react';
import debounce from 'lodash/debounce';
import { DataGrid, GridColDef, GridRenderCellParams, GridPaginationModel } from '@mui/x-data-grid';
import {
  IconButton,
  CircularProgress,
  TextField,
  Box,
  Tooltip,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import BadgeIcon from '@mui/icons-material/Badge';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import UserRolesDialog from '../components/UserRolesDialog.tsx';
import GridEmpty from '../components/GridEmpty.tsx';
import HrProfileModal from '../components/hr/HrProfileModal.tsx';
import { deleteUser, getUsersWithHr } from '../services/apiService.ts';
import { patchEmployeeProfile } from '../services/employees.ts';
import { toastSuccess, toastError } from '../shared/ui/toast.ts';
import { UserWithHr } from '../types/user.ts';
import { EmployeeStatus } from '../types/hr.ts';
import useScrollRestore from '../hooks/useScrollRestore.ts';

type StatusFilter = 'ALL' | 'NO_PROFILE' | EmployeeStatus;

const UsersPage: React.FC = () => {
  useScrollRestore('users_scroll_pos');
  const [params] = useSearchParams();
  const searchParam = params.get('search') || '';
  const [searchInput, setSearchInput] = useState('');
  const [jobTitleInput, setJobTitleInput] = useState('');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [jobTitleFilter, setJobTitleFilter] = useState('');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20,
  });

  // Persist filters in localStorage
  const STORAGE_KEY = 'vh_users_filters';

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSearchInput(parsed.search || '');
        setQuery(parsed.search || '');
        setStatusFilter(parsed.status || 'ALL');
        setJobTitleInput(parsed.jobTitle || '');
        setJobTitleFilter(parsed.jobTitle || '');
        setPaginationModel((prev) => ({ ...prev, pageSize: parsed.pageSize || prev.pageSize }));
        // sort is passed to API; not stored in state here
      } catch {
        // ignore broken saved state
      }
    }
  }, []);

  useEffect(() => {
    if (!searchParam) {
      return;
    }
    setSearchInput(searchParam);
    setQuery(searchParam);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [searchParam]);

  useEffect(() => {
    const payload = {
      search: query,
      status: statusFilter,
      jobTitle: jobTitleFilter,
      pageSize: paginationModel.pageSize,
      sort: 'lastName,asc',
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  }, [query, statusFilter, jobTitleFilter, paginationModel.pageSize]);

  // Debounced search handlers
  const debouncedSetQuery = useMemo(
    () => debounce((value: string) => {
      setQuery(value);
      setPaginationModel(prev => ({ ...prev, page: 0 })); // Reset to first page on search
    }, 500),
    []
  );

  const debouncedSetJobTitle = useMemo(
    () => debounce((value: string) => {
      setJobTitleFilter(value);
      setPaginationModel(prev => ({ ...prev, page: 0 })); // Reset to first page on search
    }, 500),
    []
  );

  // Cleanup debounced functions on unmount
  useEffect(() => {
    return () => {
      (debouncedSetQuery as any).cancel?.();
      (debouncedSetJobTitle as any).cancel?.();
    };
  }, [debouncedSetQuery, debouncedSetJobTitle]);
  const [dialogUser, setDialogUser] = useState<any | null>(null);
  const [hrProfileUser, setHrProfileUser] = useState<{
    id: number;
    name?: string;
    profile?: any | null;
  } | null>(null);
  // toast via react-toastify helpers
  
  // Data states
  const [usersData, setUsersData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // States for inline editing
  const [editingJobTitle, setEditingJobTitle] = useState<{ userId: number; value: string } | null>(null);
  const [updatingField, setUpdatingField] = useState<{ userId: number; field: string } | null>(null);

  // Fetch on mount and when filters change
  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const data = await getUsersWithHr({
          search: query || undefined,
          status: statusFilter === 'ALL' || statusFilter === 'NO_PROFILE' ? undefined : statusFilter,
          jobTitle: jobTitleFilter || undefined,
          page: paginationModel.page,
          size: paginationModel.pageSize,
          sort: 'lastName,asc'
        });
        setUsersData(data);
      } catch (error: any) {
        toastError(error.message || 'Failed to fetch users');
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [query, statusFilter, jobTitleFilter, paginationModel]);

  // Refetch function
  const refetch = () => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const data = await getUsersWithHr({
          search: query || undefined,
          status: statusFilter === 'ALL' || statusFilter === 'NO_PROFILE' ? undefined : statusFilter,
          jobTitle: jobTitleFilter || undefined,
          page: paginationModel.page,
          size: paginationModel.pageSize,
          sort: 'lastName,asc'
        });
        setUsersData(data);
      } catch (error: any) {
        toastError(error.message || 'Failed to fetch users');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  };

  // Extract rows and pagination info
  const rows = usersData?.content || usersData || [];
  const totalElements = usersData?.totalElements || rows.length;
  
  // Additional client-side filter for NO_PROFILE (since backend doesn't support this)
  const filteredRows = statusFilter === 'NO_PROFILE' 
    ? rows.filter((user: UserWithHr) => !user.hrProfile)
    : rows;

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'ON_LEAVE':
        return 'warning';
      case 'LEFT':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleJobTitleSave = async (userId: number) => {
    if (!editingJobTitle || editingJobTitle.userId !== userId) return;

    const originalValue = rows.find((r: UserWithHr) => r.userId === userId)?.hrProfile?.jobTitle || '';
    setUpdatingField({ userId, field: 'jobTitle' });
    
    try {
      await patchEmployeeProfile(userId, { jobTitle: editingJobTitle.value });
      toastSuccess('Job title updated successfully');
      refetch();
      setEditingJobTitle(null);
    } catch (error: any) {
      // Revert to original value on error
      toastError(error.message || 'Failed to update job title');
      setEditingJobTitle({ userId, value: originalValue });
    } finally {
      setUpdatingField(null);
    }
  };

  const handleStatusChange = async (userId: number, newStatus: EmployeeStatus) => {
    setUpdatingField({ userId, field: 'status' });
    
    try {
      await patchEmployeeProfile(userId, { status: newStatus });
      toastSuccess('Status updated successfully');
      refetch();
    } catch (error: any) {
      toastError(error.message || 'Failed to update status');
      // The UI will revert automatically on refetch
      refetch();
    } finally {
      setUpdatingField(null);
    }
  };

  const handleExportCSV = async () => {
    try {
      toastSuccess('Preparing CSV export...');
      
      // Fetch all data with current filters (but with a large page size)
      const allDataResponse = await getUsersWithHr({
        search: query || undefined,
        status: statusFilter === 'ALL' || statusFilter === 'NO_PROFILE' ? undefined : statusFilter,
        jobTitle: jobTitleFilter || undefined,
        page: 0,
        size: 10000, // Large size to get all records
        sort: 'lastName,asc'
      });
      
      const allData = allDataResponse?.content || allDataResponse || [];
      
      // Apply additional client-side filter for NO_PROFILE if needed
      const filteredData = statusFilter === 'NO_PROFILE' 
        ? allData.filter((user: UserWithHr) => !user.hrProfile)
        : allData;

      // Convert to CSV
      const headers = ['Name', 'Email', 'Job Title', 'Status', 'Employment Start Date', 'Work Schedule', 'Notes'];
      const csvContent = [
        headers.join(','),
        ...filteredData.map((user: UserWithHr) => {
          const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
          const name = `"${fullName}"`;
          const email = `"${user.email}"`;
          const jobTitle = user.hrProfile?.jobTitle ? `"${user.hrProfile.jobTitle}"` : '""';
          const status = user.hrProfile?.status || '';
          const startDate = user.hrProfile?.employmentStartDate || '';
          const workSchedule = user.hrProfile?.workSchedule ? `"${user.hrProfile.workSchedule}"` : '""';
          const notes = user.hrProfile?.notes ? `"${user.hrProfile.notes.replace(/"/g, '""')}"` : '""';
          
          return [name, email, jobTitle, status, startDate, workSchedule, notes].join(',');
        })
      ].join('\n');

      // Create and download file with timestamp
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
      const timeStr = now.toTimeString().slice(0, 5).replace(':', '');
      link.setAttribute('href', url);
      link.setAttribute('download', `users_${dateStr}_${timeStr}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toastSuccess('Export completed successfully');
    } catch (error: any) {
      toastError('Failed to export CSV: ' + error.message);
    }
  };

  const cols: GridColDef[] = [
    { field: 'userId', headerName: 'ID', width: 70 },
    {
      field: 'fullName',
      headerName: 'Name',
      flex: 1,
      valueGetter: (params) => `${params.row.firstName || ''} ${params.row.lastName || ''}`.trim(),
    },
    { field: 'email', headerName: 'Email', flex: 1 },
    { field: 'roles', headerName: 'Roles', width: 150, valueGetter: (p) => p.row.roles?.join(', ') },
    {
      field: 'jobTitle',
      headerName: 'Job Title',
      width: 200,
      renderCell: (params: GridRenderCellParams) => {
        const user = params.row as UserWithHr;
        const isEditing = editingJobTitle?.userId === user.userId;
        const isUpdating = updatingField?.userId === user.userId && updatingField.field === 'jobTitle';
        
        if (!user.hrProfile) {
          return <span style={{ fontStyle: 'italic', color: 'grey' }}>N/A</span>;
        }

        if (isUpdating) {
          return <CircularProgress size={20} />;
        }

        if (isEditing) {
          return (
            <Box display="flex" alignItems="center" gap={0.5}>
              <TextField
                size="small"
                value={editingJobTitle?.value || ''}
                onChange={(e) => setEditingJobTitle({ userId: user.userId, value: e.target.value })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleJobTitleSave(user.userId);
                  if (e.key === 'Escape') setEditingJobTitle(null);
                }}
                autoFocus
                sx={{ width: 120 }}
              />
              <IconButton size="small" onClick={() => handleJobTitleSave(user.userId)}>
                <SaveIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => setEditingJobTitle(null)}>
                <CancelIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        }

        return (
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <span>{user.hrProfile.jobTitle}</span>
            <IconButton 
              size="small" 
              onClick={() => setEditingJobTitle({ userId: user.userId, value: user.hrProfile?.jobTitle || '' })}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        );
      },
    },
    {
      field: 'status',
      headerName: 'HR Status',
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const user = params.row as UserWithHr;
        const isUpdating = updatingField?.userId === user.userId && updatingField.field === 'status';
        
        if (!user.hrProfile?.status) {
          return <span style={{ fontStyle: 'italic', color: 'grey' }}>-</span>;
        }

        if (isUpdating) {
          return <CircularProgress size={20} />;
        }

        return (
          <Select
            value={user.hrProfile.status}
            onChange={(e: SelectChangeEvent) => handleStatusChange(user.userId, e.target.value as EmployeeStatus)}
            size="small"
            variant="standard"
            sx={{ 
              '& .MuiSelect-select': { 
                py: 0.5,
                pr: 3,
                backgroundColor: 'transparent',
              }
            }}
            renderValue={(value) => (
              <Chip
                label={value.replace('_', ' ')}
                size="small"
                color={getStatusColor(value) as any}
              />
            )}
          >
            {(['ACTIVE', 'ON_LEAVE', 'LEFT'] as const).map((status) => (
              <MenuItem key={status} value={status}>
                <Chip
                  label={status.replace('_', ' ')}
                  size="small"
                  color={getStatusColor(status) as any}
                />
              </MenuItem>
            ))}
          </Select>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      renderCell: (params) => {
        const user = params.row as UserWithHr;
        return (
          <>
            <Tooltip title="HR Profile">
              <IconButton
                color="primary"
                onClick={() =>
                  setHrProfileUser({
                    id: user.userId,
                    name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
                    profile: user.hrProfile ? {
                      jobTitle: user.hrProfile.jobTitle,
                      employmentStartDate: user.hrProfile.employmentStartDate,
                      status: user.hrProfile.status,
                      emergencyContactName: user.hrProfile.emergencyContactName,
                      emergencyContactPhone: user.hrProfile.emergencyContactPhone,
                      workSchedule: user.hrProfile.workSchedule,
                      notes: user.hrProfile.notes,
                    } : null
                  })
                }
              >
                <BadgeIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit roles">
              <IconButton onClick={() => setDialogUser({
                id: user.userId,
                email: user.email,
                roles: user.roles
              })}>
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete user">
              <IconButton
                color="error"
                onClick={async () => {
                  try {
                    await deleteUser(user.userId);
                    toastSuccess('User deleted');
                    refetch();
                  } catch (e: any) {
                    const msg = e?.response?.data?.message || e?.response?.data?.error || e.message;
                    toastError(msg);
                  }
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
  ];

  return (
    <Box p={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" gap={2} alignItems="center">
          <TextField
            size="small"
            label="Search Name/Email"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              debouncedSetQuery(e.target.value);
            }}
          />
          <TextField
            size="small"
            label="Filter by Job Title"
            value={jobTitleInput}
            onChange={(e) => {
              setJobTitleInput(e.target.value);
              debouncedSetJobTitle(e.target.value);
            }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Filter by Status</InputLabel>
            <Select
              value={statusFilter}
              label="Filter by Status"
              onChange={(e) => {
                setStatusFilter(e.target.value as StatusFilter);
                setPaginationModel(prev => ({ ...prev, page: 0 })); // Reset to first page on filter change
              }}
            >
              <MenuItem value="ALL">All</MenuItem>
              <MenuItem value="NO_PROFILE">No HR Profile</MenuItem>
              {(['ACTIVE', 'ON_LEAVE', 'LEFT'] as const).map((status) => (
                <MenuItem key={status} value={status}>
                  {status.replace('_', ' ')}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleExportCSV}
        >
          Export CSV
        </Button>
      </Box>
      {loading ? (
        <CircularProgress />
      ) : (
        <DataGrid
          rows={filteredRows}
          columns={cols}
          autoHeight
          getRowId={(r) => r.userId}
          components={{ NoRowsOverlay: GridEmpty }}
          paginationMode="server"
          rowCount={totalElements}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50, 100]}
        />
      )}
      {dialogUser && (
        <UserRolesDialog open={true} onClose={() => setDialogUser(null)} user={dialogUser} onSaved={refetch} />
      )}

      {hrProfileUser && (
        <HrProfileModal
          open={true}
          onClose={() => setHrProfileUser(null)}
          userId={hrProfileUser.id}
          userName={hrProfileUser.name}
          initialProfile={hrProfileUser.profile}
          onSuccess={refetch}
        />
      )}
    </Box>
  );
};

export default UsersPage;
